<template>
  <div class="grid-container my-6 mt-14">
    <div class="flex flex-col gap-4 md:justify-center">
      <div class="flex flex-col justify-center md:text-center">
        <h1 class="pb-2 headline-md md:headline-lg">Completa tu perfil</h1>
        <p class="title-md md:title-lg">
          Haz que tu perfil sea más atractivo para reclutadores en tres simples pasos
        </p>
      </div>
      <!-- Navigation -->
      <div id="step-navigation-container" class="flex justify-around md:justify-center md:gap-24">
        <button
          v-for="item in lastStep"
          :key="item"
          :class="[
            'decoration-none rounded-full text-center py-3 px-4 cursor-pointer text-on-tertiary-container transition-all duration-300 hover:transition-all hover:duration-300',
            currentStep === item
              ? ' bg-tertiary text-on-tertiary cursor-default'
              : 'bg-tertiary-container hover:elevation-2 active:shadow-none active:bg-tertiary80',
          ]"
          @click="currentStep = item"
        >
          <span class="block text-overline">Paso</span>
          <span class="block text-chips">{{ item }}</span>
        </button>
      </div>
      <div id="step-container" class="md:flex md:flex-col md:justify-center md:items-center">
        <keep-alive>
          <component
            class="md:w-3/5"
            :is="currentStepConfig.component"
            :config="currentStepConfig"
            @update-content="updateContent"
          />
        </keep-alive>
        <div
          v-if="errors && Object.entries(errors).length > 0"
          class="md:w-3/5 block text-center my-4 border border-error bg-error-container text-on-error-container p-4 rounded-2xl"
        >
          <p class="label-lg">¡Ocurrio un error al guardar el perfil!</p>
          <ul>
            <li v-for="(error, key) in errors" :key="key">
              {{ error }}
            </li>
          </ul>
        </div>
        <div
          v-if="currentStep === lastStep"
          class="md:w-3/5 block text-center my-4 border border-tertiary bg-tertiary-container/30 text-on-tertiary-container p-4 rounded-2xl"
        >
          <p class="label-lg">¿Has terminado?</p>
          <p class="body-md text-neutral30 mb-2">
            Haz click en el siguiente botón para guardar los cambios a tu perfil.
          </p>
          <div class="mt-1">
            <Button
              type="button"
              class="btn-filled brand-green !bg-brand-dark-green"
              @click="uploadProfile"
            >
              Guardar mi perfil
            </Button>
          </div>
        </div>
      </div>
      <!-- Bottom navigation -->
      <div class="flex flex-wrap justify-around md:justify-center md:gap-6">
        <Button
          v-if="currentStep !== 1"
          class="block btn-tonal"
          icon="arrow-back"
          @click="handlePrevStep"
          >Atrás</Button
        >
        <Button
          v-if="currentStep !== lastStep"
          class="block btn-tonal"
          icon="arrow-forward"
          icon-orientation="end"
          @click="handleNextStep"
          >Siguiente</Button
        >
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '@/Layouts/Layout'
import Button from '@/Components/Button.vue'

export default {
  name: 'ApplicantProfileCreate',
  layout: Layout,
  components: {
    PasoUno: () => import('@/Components/CrearPerfil/PasoUno.vue'),
    PasoDos: () => import('@/Components/CrearPerfil/PasoDos.vue'),
    PasoTres: () => import('@/Components/CrearPerfil/PasoTres.vue'),
    Button,
  },
  props: {
    errors: Object,
    profile: Object,
    educational_degrees_options: Array,
    educational_institutions_options: Array,
    study_fields_options: Array,
    language_options: Array,
    language_proficiency_options: Array,
  },
  data() {
    return {
      isUploadingProfile: false,
      currentStep: 1,
      lastStep: 3,
      content: {
        profile: this.profile,
        education: [],
        languages: [],
        job_experience: [],
        skills: [],
        cv_file: null,
      },
    }
  },
  methods: {
    scrollTo(element) {
      const $element = document.getElementById(element)
      if ($element === null) console.warning(`${element} is not registered.`)
      else $element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },
    handleNextStep() {
      this.scrollTo('step-navigation-container')
      if (this.currentStep < this.lastStep) this.currentStep++
    },
    handlePrevStep() {
      this.scrollTo('step-navigation-container')
      if (this.currentStep > 1) this.currentStep--
    },
    cleanNullValuesFromObject(objectToClean) {
      return Object.entries(objectToClean).reduce((prev, [key, value]) => {
        if (value !== '' && value !== null && typeof value !== 'undefined') prev[key] = value
        return prev
      }, {})
    },
    cleanContent() {
      const cleanNullValuesFromObject = this.cleanNullValuesFromObject
      return Object.entries(this.content).reduce(
        function(reducedObject, [key, value]) {
          if (key === 'cv_file' && value !== null) {
            reducedObject['cv_file'] = value
          } else if (key === 'profile') {
            // Clean profile object
            reducedObject.profile = {
              name: value.name,
              lastname: value.lastname,
              personal_id: value.personal_id,
              date_of_birth: value.date_of_birth,
              gender: value.gender,
            }
            reducedObject.profile = cleanNullValuesFromObject(reducedObject.profile)
          } else if (
            Array.isArray(value) &&
            ['education', 'languages', 'job_experience', 'skills'].includes(key)
          ) {
            reducedObject[key] = value.map(item => cleanNullValuesFromObject(item))
          }
          return reducedObject
        },
        {} /* <-- Initiating the reducedObject */,
      )
    },
    updateContent(contentKey, content) {
      this.content[contentKey] = content
    },
    uploadProfile() {
      this.$swal.fire({
        title: 'Guardando cambios...',
        html: `
            <div class="flex gap-2 items-center">
              <svg v-else-if="name === 'loading'" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" width="24" height="24" viewBox="0 0 24 24" preserveAspectRatio="xMidYMid meet" ><path fill="currentColor" d="M12 2A10 10 0 1 0 22 12A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8A8 8 0 0 1 12 20Z" opacity=".5"/><path fill="currentColor" d="M20 12h2A10 10 0 0 0 12 2V4A8 8 0 0 1 20 12Z"><animateTransform attributeName="transform" dur="1s" from="0 12 12" repeatCount="indefinite" to="360 12 12" type="rotate"/></path></svg>
              <p>Espera un momento, por favor.</p>
            </div>
          `,
        showConfirmButton: false,
      })

      this.$inertia.post(this.$route('profile.update'), this.cleanContent(), {
        preserveState: true,
        onError: errors => {
          this.$swal.fire({
            title: '¡Error!',
            text: 'Parece que ha ocurrido un error. Revisa los datos e intenta de nuevo.',
          })
        },
        onSuccess: () => {
          this.$swal.fire({
            title: '¡Perfil actualizado con éxito!',
            text: 'Tus datos se guardaron con éxito',
          })
        },
      })
    },
  },
  computed: {
    currentStepConfig() {
      if (this.currentStep == 1)
        return {
          component: 'PasoUno',
          form_data: this.content.profile,
        }
      if (this.currentStep == 2)
        return {
          component: 'PasoDos',
          form_data: {
            education: this.content.education,
            languages: this.content.languages,
          },
          options_data: {
            education: {
              educational_degrees_options: this.educational_degrees_options,
              educational_institutions_options: this.educational_institutions_options,
              study_fields_options: this.study_fields_options,
            },
            language: {
              language_options: this.language_options,
              language_proficiency_options: this.language_proficiency_options,
            },
          },
        }
      if (this.currentStep == 3)
        return {
          component: 'PasoTres',
          form_data: {
            job_experience: this.content.job_experience,
            skills: this.content.skills,
          },
        }
    },
  },
}
</script>

<style></style>
